import { TrackedEvent, trackEvent } from "@/domains/metrics";
import {
  MDSListBox,
  MDSIconButton,
  MDSText,
  MDSTextWeight,
  MDSTextVariant,
  MDSIcon,
  MDSIconKind,
} from "@/mds";
import { MDSListBoxItemProps } from "@/mds/components/lists/list-box-item";
import { copyTextToClipboard } from "@/modules/clipboard/copyTextToClipboard";
import { toastModule } from "@/modules/toast";
import { uuidModule } from "@/modules/uuid";
import { IntegrationsApiPageContentForm } from "@/pages/integrations/api/integrations-api-page-content-form";
import { DeleteKeyModal } from "@/pages/integrations/api/integrations-api-page-delete-modal";
import {
  ApiKeyItemInfo,
  PendingApiKey,
} from "@/pages/integrations/api/integrations-api-page.types";
import { IntegrationsLayoutBottomBuffer } from "@/pages/integrations/shared/integrations-layout";
import { StyledHeading } from "@/pages/integrations/shared/integrations.styles";
import { AppStore, useAppStore } from "@/store";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

type Props = {};

export const ApiKeyItemList: FC<Props> = observer(() => {
  const { store } = useAppStore();

  const [pendingApiKey, setPendingApiKey] = useState<PendingApiKey | null>(null);
  const [keyToDelete, setKeyToDelete] = useState<ApiKeyItemInfo | null>(null);
  const [formShown, setFormShown] = useState(false);
  const [nickname, setNickname] = useState("");

  const isLoading = store.apiKeys.isCreatingApiKey;

  const createApiKey = useCallback(async () => {
    const id = uuidModule.generate();
    const secretKeyToken = uuidModule.generate();

    await store.apiKeys.createApiKey({
      id,
      secretKeyToken,
      // Title and description are not set for now.
      title: null,
      description: null,
    });

    trackEvent(TrackedEvent.ApiKeyCreate, {
      api_key_id: id,
    });

    setPendingApiKey({ id, secretKeyToken });
  }, [store.apiKeys]);

  useEffect(() => {
    // When isCreatingApiKey becomes false and we have a pending key, show the modal
    if (!store.apiKeys.isCreatingApiKey && pendingApiKey) {
      setFormShown(true);
    }
  }, [store.apiKeys.isCreatingApiKey, pendingApiKey]);

  const onCreateHandler = useCallback(() => {
    if (isLoading) {
      return;
    }

    createApiKey();
  }, [createApiKey, isLoading]);

  const copyApiKey = useCallback(() => {
    if (pendingApiKey) {
      copyTextToClipboard({ text: `sk-mem-${pendingApiKey.secretKeyToken}` });
      toastModule.triggerToast({ content: "API key copied to clipboard" });
    }
  }, [pendingApiKey]);

  const deleteApiKey = useCallback((info: ApiKeyItemInfo) => {
    setKeyToDelete(info);
  }, []);

  const handleCloseForm = () => {
    setFormShown(false);
    setPendingApiKey(null);
    setNickname("");
  };

  const { items } = useItems({
    store,
    formShown,
    isLoading,
    pendingApiKey,
    onCreateHandler,
    deleteApiKey,
  });

  return (
    <Wrapper listHasItems={items.length > 0}>
      <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
        Activated API keys
      </StyledHeading>
      <MDSListBox items={items} />
      <DeleteKeyModal keyToDelete={keyToDelete} setKeyToDelete={setKeyToDelete} />

      {formShown && (
        <IntegrationsApiPageContentForm
          nickname={nickname}
          pendingApiKey={pendingApiKey}
          copyApiKey={copyApiKey}
          handleCloseForm={handleCloseForm}
          setNickname={setNickname}
        />
      )}
      <IntegrationsLayoutBottomBuffer />
    </Wrapper>
  );
});

const useItems = ({
  store,
  formShown,
  isLoading,
  pendingApiKey,
  onCreateHandler,
  deleteApiKey,
}: {
  store: AppStore;
  formShown: boolean;
  isLoading: boolean;
  pendingApiKey: PendingApiKey | null;
  onCreateHandler: () => void;
  deleteApiKey: (info: ApiKeyItemInfo) => void;
}) => {
  const apiKeyInfoList: ApiKeyItemInfo[] = useMemo(() => {
    return store.apiKeys.allActiveApiKeys
      .filter(apiKey => apiKey.id !== pendingApiKey?.id)
      .map(apiKey => {
        return {
          id: apiKey.id,
          title: apiKey.title,
          createdAt: apiKey.createdAt,
          redactedToken: apiKey.redactedToken,
        };
      })
      .sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis());
  }, [store.apiKeys.allActiveApiKeys, pendingApiKey]);

  const items: MDSListBoxItemProps[] = apiKeyInfoList.map(info => ({
    id: info.id,
    textValue: info.title + " " + info.redactedToken,
    leftAddon: (
      <TitleText>
        <MDSText variant={MDSTextVariant.TextS}>{info.title}</MDSText>
      </TitleText>
    ),
    children: (
      <SecretTokenText>
        <MDSText variant={MDSTextVariant.TextS}>{info.redactedToken}</MDSText>
      </SecretTokenText>
    ),
    rightAddon: <MDSIconButton iconKind={MDSIconKind.Trash} onClick={() => deleteApiKey(info)} />,
  }));

  if (!formShown) {
    items.push({
      id: "create-api-key",
      textValue: "Create API key",
      onAction: onCreateHandler,
      isDisabled: isLoading,
      leftAddon: (
        <IconWrapper>
          <MDSIcon kind={MDSIconKind.Plus} size={16} />
        </IconWrapper>
      ),
      children: (
        <MDSText variant={MDSTextVariant.TextS}>
          {isLoading ? "Creating API key..." : "Add new API key"}
        </MDSText>
      ),
    });
  }

  return { items };
};

const Wrapper = styled.div<{ listHasItems: boolean }>(({ theme, listHasItems }) => ({
  width: "100%",
  marginTop: theme.spacing["2"],
  padding: 0,
  display: "flex",
  height: "100%",
  flexDirection: "column",
  gap: listHasItems ? theme.spacing["2"] : theme.spacing["1"],
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const TitleText = styled.div(({ theme }) => ({
  width: "120px",
  height: 36,
  [theme.mediaQueries.rules.phone]: {
    width: "50px",
  },
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  display: "flex",
  alignItems: "center",
  paddingLeft: theme.spacing[2],
}));

const SecretTokenText = styled.div(() => ({
  fontFamily: "monaco, monospace",
}));

const IconWrapper = styled.div<{ variant?: "primary" | "secondary" }>(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing[2],

  height: 32,
  width: 32,
}));
